import { api } from 'domains/api';
import { EnterInsuranceFormV4 } from 'domains/signup/data/types';
import { EligibilityCheckResponse, EligibilityCheckResult } from './types';
import { BASE_ANONYMOUS_API_ROUTES, getBaseAPIRoute } from 'domains/api/base/routes';
import { isLeadEligible } from './isLeadEligible';
import * as Sentry from '@sentry/react';
import { TypeInsuranceFields } from 'types/contentful';
import { isAxiosError } from 'axios';
import { get } from 'lodash';
import { RelationshipCode } from 'domains/signupDirect/businessLogic/relationships';

export interface EligibilityCheckPatientParams {
  memberID: string;
  dateOfBirth?: string;
  firstName?: string;
  lastName?: string;
  middleName?: string;
  relationshipToClient?: RelationshipCode;
}

export interface EligibilityCheckParams {
  payerID: string;
  payerIDOverride?: string;
  npi?: string;
  state: string;
  dateOfService?: string;
  subscriber: EligibilityCheckPatientParams;
}

export const mapSignupV4ToMedicaidInsuranceForm = (payload?: EnterInsuranceFormV4): EligibilityCheckParams => {
  if (!payload) {
    throw new Error('bad state from insurance form');
  }

  return {
    payerID: payload.insurancePlan,
    npi: payload.npi || undefined,
    state: payload.state,
    subscriber: {
      memberID: payload.memberId,
      dateOfBirth: new Date(payload?.dob || '').toISOString(),
      firstName: payload.firstName,
      lastName: payload.lastName,
    },
  };
};

const getStediErrorResponseStatus = (error: unknown) => {
  if (isAxiosError(error)) return EligibilityCheckResult.ERROR;

  const errorType = get<any, string>(error, 'originalError.response.data.message', EligibilityCheckResult.ERROR);

  if (errorType == EligibilityCheckResult.ERROR) return errorType;

  switch (true) {
    case errorType?.includes('Patient Birth Date Does Not Match That for the Patient on the Database') ||
      errorType?.includes('DOB was not sent in the request and is required by the payer to locate the member'):
      return EligibilityCheckResult.INCORRECT_DOB;
    case errorType?.includes('Incorrect subscriber name submitted') ||
      errorType.includes('Subscriber name spelled incorrectly'):
      return EligibilityCheckResult.INCORRECT_NAME;
    case errorType?.includes('Subscriber member ID  was incorrect in the request'):
      return EligibilityCheckResult.INCORRECT_MEMBER_ID;
    case errorType?.includes('Subscriber/Insured Not Found'):
      return EligibilityCheckResult.INCORRECT_MEMBER_ID;
    case errorType?.includes('Invalid Participant Identification'):
    default:
      return EligibilityCheckResult.ERROR;
  }
};

export const checkEligibility = ({
  checkParams,
  insuranceAllowSelfSignUp,
  insuranceInNetwork,
}: {
  checkParams: EligibilityCheckParams;
  insuranceInNetwork: TypeInsuranceFields['networkStatus'];
  insuranceAllowSelfSignUp: TypeInsuranceFields['allowSelfSignUp'];
}) => {
  if (insuranceInNetwork === 'Out-of-network') {
    return Promise.resolve(EligibilityCheckResult.OUT_OF_NETWORK);
  }

  return api.baseAnonymous
    .post<EligibilityCheckResponse>(getBaseAPIRoute(BASE_ANONYMOUS_API_ROUTES.eligibility.check), checkParams)
    .then(response => {
      return isLeadEligible(response);
    })
    .catch(error => {
      Sentry.captureException('A lead responded with an error from eligibility check', {
        extra: {
          error,
          checkParams,
          insuranceInNetwork,
          insuranceAllowSelfSignUp,
        },
      });

      return getStediErrorResponseStatus(error);
    });
};
